/**
 * Concordância de número, para texto que a tela monta com um valor do servidor.
 *
 * Existe porque com dados de mentira o defeito não aparece: nas prévias os
 * números eram 428 e 311, e o plural estava certo por acidente. Contra a conta
 * de verdade, com duas respostas, a tela dizia **"1 certas de 2 respondidas"**.
 *
 * Zero vira "nenhuma", e não "0": "0 questões" lê como defeito de programa, e
 * "nenhuma questão" é como se fala.
 *
 * **O gênero do zero é escolha de quem chama.** O padrão feminino nasceu com
 * "questão" e "resolução", e ficou escondido até a Legislação Comentada contar
 * artigos: "1073 artigos - nenhuma comentado" foi o que apareceu na tela. Não dá
 * para deduzir gênero de uma palavra em português sem uma lista de exceções, e
 * lista de exceções é pior que um parâmetro.
 */
export function contar(
  quantas: number,
  singular: string,
  plural: string,
  zero: 'nenhuma' | 'nenhum' = 'nenhuma',
): string {
  if (quantas === 0) return `${zero} ${singular}`;
  return `${emMilhar(quantas)} ${quantas === 1 ? singular : plural}`;
}

/**
 * O separador de milhar do português.
 *
 * `2083 artigos` é número de programa; `2.083 artigos` é número. Apareceu na
 * Legislação Comentada, onde o Código Civil tem 2.083 artigos e o Código de
 * Processo Civil, 3.921 - e some em qualquer tela cujos dados de teste sejam
 * menores que mil.
 */
export function emMilhar(quantos: number): string {
  return quantos.toLocaleString('pt-BR');
}

/**
 * `1` -> `Art. 1º`, `1.245` -> `Art. 1.245`.
 *
 * **Só a exibição muda.** A chave do artigo no acervo é `1`, e é ela que o
 * verificador de citações procura e que o `commented_articles` guarda: mexer
 * nela quebraria a incidência e a fila do comentário. Mas a forma brasileira
 * ordena até o nono - `Art. 1º`, não `Art. 1` -, e num produto que vende
 * precisão jurídica isso se lê como erro.
 */
export function comoSeCitaOArtigo(article: string): string {
  return /^[1-9](-[A-Z])?$/.test(article) ? `Art. ${article[0]}º${article.slice(1)}` : `Art. ${article}`;
}

/** Só a palavra, para quando o número já está em outro lugar da frase. */
export function palavra(quantas: number, singular: string, plural: string): string {
  return quantas === 1 ? singular : plural;
}

/**
 * "Restam N x", com o verbo concordando - e sem o zero esquisito.
 *
 * Medido no navegador em 04/09/2026, com uma conta gratuita de verdade: a tela
 * de estudo dizia **"Restam 1 respostas hoje"** e **"Restam 0 resoluções hoje"**.
 * O verbo estava fixo e o número vinha do servidor, então o defeito só aparece
 * quando a cota chega perto do fim - que é exatamente quando a pessoa olha.
 *
 * Zero não vira "Restam nenhuma": vira "Sem", porque é o que se fala e porque a
 * frase precisa caber num `Tag`.
 */
export function restam(quantas: number, singular: string, plural: string): string {
  if (quantas === 0) return `Sem ${plural} nesta semana`;
  return `${palavra(quantas, 'Resta', 'Restam')} ${quantas} ${palavra(quantas, singular, plural)} nesta semana`;
}

/**
 * O fuso do produto é o de Brasília.
 *
 * Um acesso que termina às 21h de 1/9 em Nova York termina às 2h de 2/9 aqui, e
 * quem lê a data é quem está aqui: sem o fuso fixo, a mesma compra apareceria
 * com dias diferentes conforme o relógio do aparelho. O calendário do produto é o de
 * Brasília em todo o resto (a cota semanal, a `semana` do servidor) -
 * numa data na tela não podia ser diferente.
 */
const FUSO_DO_PRODUTO = 'America/Sao_Paulo';

/** "12 de setembro de 2027" - a data quando ela é o assunto da linha. */
export function dataPorExtenso(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: FUSO_DO_PRODUTO,
  });
}

/** "12/09/2027" - a data quando ela é um detalhe ao lado de outra coisa. */
export function dataCurta(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: FUSO_DO_PRODUTO,
  });
}
